import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import type { FiscalCalendarPeriod } from '@apple/features/calendar/models';
import { calendarQueryOptions } from '@apple/features/calendar/queries';

import type { BudgetItem } from '../components/BudgetSummary';
import type { BudgetSummaryItem, StoreStateDetails } from '../models/budget';
import { budgetQueryOptions } from '../queries/budget';

const colorThreshold = 'var(--apple-color-orange-progress-section-bg)';
const colorOverLimit = 'var(--apple-color-red-progress-section-bg)';
const colorUnderLimit = 'var(--apple-color-lime-progress-section-bg)';
const colorPending = 'var(--apple-color-blue-progress-section-bg)';
const colorRemaining = 'var(--apple-color-blue-progress-root-bg)';

function getBudgetData(
	budgetSummary: BudgetSummaryItem[],
	fiscalPeriods: FiscalCalendarPeriod[],
): BudgetItem[] {
	return budgetSummary.map(value => ({
		...value,
		fiscalPeriod: fiscalPeriods.find(y => y.periodNumber === value.periodNumber),
	}));
}

export function useBudget(
	storeCode: string,
	initialFiscalYear?: number,
	initialPeriodNumber?: number,
) {
	const currentPeriodQuery = useSuspenseQuery(calendarQueryOptions.current);
	const currentFiscalPeriod = currentPeriodQuery.data;
	const [selectedFiscalYear, setSelectedFiscalYear] = useState<string | null>(
		initialFiscalYear
			? initialFiscalYear.toString()
			: currentFiscalPeriod.fiscalYear.toString(),
	);
	const [selectedPeriodNumber, setSelectedPeriodNumber] = useState<string | null>(
		initialPeriodNumber
			? initialPeriodNumber.toString()
			: currentFiscalPeriod.periodNumber.toString(),
	);

	const fiscalYear = useMemo(() => Number(selectedFiscalYear), [selectedFiscalYear]);

	const budgetSummaryQuery = useQuery(
		budgetQueryOptions.budgetSummary(storeCode ?? '', fiscalYear),
	);
	const fiscalYearQuery = useQuery(calendarQueryOptions.fiscalYear(fiscalYear));
	const fiscalYearList = useQuery(calendarQueryOptions.list).data ?? [];

	const budgetData = useMemo(() => {
		return !budgetSummaryQuery.isFetching &&
			!fiscalYearQuery.isFetching &&
			budgetSummaryQuery.data &&
			fiscalYearQuery.data
			? getBudgetData(budgetSummaryQuery.data ?? [], fiscalYearQuery.data.fiscalPeriods)
			: [];
	}, [
		budgetSummaryQuery.data,
		budgetSummaryQuery.isFetching,
		fiscalYearQuery.data,
		fiscalYearQuery.isFetching,
	]);

	const hasData = budgetData.length > 0 || fiscalYearList.length > 0;
	return {
		currentFiscalPeriod,
		selectedFiscalYear,
		setSelectedFiscalYear,
		selectedPeriodNumber,
		setSelectedPeriodNumber,
		budgetData,
		fiscalYearList,
		hasData,
	};
}

export function useCurrentBudget({ storeState }: { storeState: StoreStateDetails }) {
	const { remainingBudget, allocatedAmount, consumedAmount, pendingAmount, periodEndDate } =
		storeState;

	const remainingDays = useMemo(() => dayjs(periodEndDate).diff(dayjs(), 'day'), [periodEndDate]);

	const consumedPercent = useMemo(() => {
		let percent = allocatedAmount > 0 ? (consumedAmount / allocatedAmount) * 100 : 100;

		if (consumedAmount === 0 && remainingBudget > 0) {
			percent = 0;
		} else if (Number.isNaN(percent) || percent < 0) {
			percent = 0;
		} else if (consumedAmount > 0 && consumedAmount <= 5 && allocatedAmount > 0) {
			percent = 5;
		}

		return consumedAmount === 0 ? 1 : percent;
	}, [allocatedAmount, consumedAmount, remainingBudget]);

	const pendingPercent = useMemo(() => {
		if (consumedPercent >= 100) {
			return 0;
		}

		let percent =
			allocatedAmount > 0 ? (pendingAmount / allocatedAmount) * 100 : consumedPercent + 100;

		if (Number.isNaN(percent) || percent < 0) {
			percent = 0;
		} else if (consumedPercent < 5 && percent > 0 && percent <= 10) {
			percent = 10;
		} else if (percent > 0 && percent <= 10) {
			percent += 2;
		}

		return pendingAmount > 0 ? percent : pendingAmount;
	}, [allocatedAmount, consumedPercent, pendingAmount]);

	const colorBudgetProgress = useMemo(() => {
		return consumedPercent >= 100
			? colorOverLimit
			: consumedPercent > 70
				? colorThreshold
				: colorUnderLimit;
	}, [consumedPercent]);

	return {
		remainingDays,
		consumedPercent,
		pendingPercent,
		colorBudgetProgress,
		colorPending,
		colorRemaining,
	};
}
